export default function AboutUs() {
  return (
    <section id="about" className="relative py-32 px-8 bg-[#0a0a0f]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(0,217,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(0,217,255,0.2) 0%, transparent 50%)',
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-6xl font-thin mb-4">
            About <span className="text-cyan-400" style={{ textShadow: '0 0 30px rgba(0,217,255,0.6)' }}>Us</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            An Australian modular pod supplier with a focus on considered design and project coordination
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left - Image */}
          <div className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-cyan-500/30">
              <img 
                src="/images/ai-modern-architectural-office-building-wit-8e75b6.webp"
                alt="Nexa Pods Office"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Floating Tag */}
            <div className="absolute -bottom-8 -right-8 bg-gradient-to-br from-[#14141e]/90 to-[#0a0a0f]/90 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6">
              <div className="text-4xl font-bold text-cyan-400" style={{ textShadow: '0 0 20px rgba(0,217,255,0.6)' }}>AU</div>
              <p className="text-gray-400 text-sm">Based in Victoria</p>
            </div>
          </div>

          {/* Right - Text Content */}
          <div className="space-y-6">
            <h3 className="text-3xl font-medium text-white leading-tight">
              Modular Pod Supply & Project Coordination
            </h3>
            <p className="text-gray-300 text-lg leading-relaxed">
              Nexa Pods brings together considered prefabricated design and practical project coordination for clients across Australia. Our team draws on broad industry experience in property, design, and project delivery to help navigate each stage — from initial site review through to handover.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              We work with a curated range of modular pod models suited to hospitality, glamping, private retreats, and flexible accommodation. Every enquiry starts with understanding your site, your goals, and your local requirements.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              The pods we supply are backed by an established manufacturing network with significant production depth — giving you confidence in build consistency and delivery capability without needing to navigate factory relationships on your own.
            </p>
          </div>
        </div>

        {/* Manufacturing Network Capability */}
        <div className="mb-20">
          <p className="text-center text-gray-500 text-sm uppercase tracking-widest mb-8">Backed by an Established Manufacturing Network</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { highlight: '2008', label: 'Manufacturing Since', sub: 'Established network', icon: 'ri-calendar-line' },
              { highlight: '50,000 m²', label: 'Production Space', sub: 'Two facilities · 200+ stations', icon: 'ri-building-line' },
              { highlight: '200+', label: 'Monthly Capacity', sub: 'Unit delivery capability', icon: 'ri-ship-line' },
              { highlight: '100+', label: 'Projects Completed', sub: 'AU & international markets', icon: 'ri-global-line' },
            ].map((stat, index) => (
              <div key={index} className="bg-gradient-to-br from-[#14141e]/60 to-[#0a0a0f]/60 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6 text-center hover:border-cyan-400/60 transition-all hover:shadow-[0_0_30px_rgba(0,217,255,0.2)]">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-3xl text-cyan-400`}></i>
                </div>
                <div className="text-4xl font-bold text-cyan-400 mb-2" style={{ textShadow: '0 0 20px rgba(0,217,255,0.6)' }}>
                  {stat.highlight}
                </div>
                <p className="text-gray-300 text-sm font-medium">{stat.label}</p>
                <p className="text-gray-500 text-xs mt-1">{stat.sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Content */}
        <div className="bg-gradient-to-br from-[#14141e]/60 to-[#0a0a0f]/60 backdrop-blur-xl border border-cyan-500/30 rounded-2xl p-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-medium text-white mb-6">Our Approach</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Every project begins with a conversation about your site, your intended use, and your local approval pathway. We coordinate the supply of modular pods — drawing on an established manufacturing network with proven production scale — and can assist with connecting you to local professionals for site preparation, approvals, transport, and installation. Conditions and requirements differ from one location to the next, and we help you work through them step by step.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Pod specifications, lead times, delivery logistics, and installation duration all vary by model, destination, and site readiness. We provide realistic timelines and transparent information so you can plan with confidence from the start.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: 'ri-shield-check-line', title: 'Design Review', desc: 'Site & model suitability' },
                { icon: 'ri-file-list-3-line', title: 'Clear Information', desc: 'Transparent specifications' },
                { icon: 'ri-lightbulb-line', title: 'Coordination', desc: 'End-to-end project support' },
                { icon: 'ri-chat-3-line', title: 'Direct Contact', desc: 'Talk to our team directly' },
              ].map((item, index) => (
                <div key={index} className="bg-black/30 rounded-xl p-5 border border-gray-800 hover:border-cyan-500/30 transition-all">
                  <div className="w-10 h-10 flex items-center justify-center mb-3">
                    <i className={`${item.icon} text-2xl text-cyan-400`}></i>
                  </div>
                  <h4 className="text-white font-medium mb-1">{item.title}</h4>
                  <p className="text-gray-500 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}